import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  CrisisBanner,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title: "Crisis Resources | Neuro Wellness TMS Centers of America",
  description:
    "If you're in crisis, call or text 988 (Suicide & Crisis Lifeline, 24/7) or call 911. Resources for immediate help and urgent mental health care.",
  canonical: "/crisis-resources",
  keywords:
    "crisis resources, suicide prevention, 988 crisis line, mental health crisis Florida, emergency mental health",
};

const relatedLinks = [
  {
    title: "Suicidal Ideation Treatment",
    href: "/suicidal-ideation-treatment-coral-springs-fl",
  },
  { title: "SPRAVATO for TRD", href: "/services/spravato" },
  { title: "Schedule a Consultation", href: "/contactus" },
  { title: "Am I a Candidate?", href: "/am-i-a-candidate" },
];

export default function CrisisResourcesPage() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Crisis Resources"
      title="If You're in Crisis Right Now"
      subtitle="This page is for people who need help immediately. The information below is not a substitute for emergency care."
      ctaHeading="When You're Ready, We're Here"
      ctaText="If you're not in immediate danger but want to talk about treatment options, call us at (954) 827-9793. We'll help you understand what may be available."
    >
      <CrisisBanner variant="light" />

      <div className="pt-6">
        <DecisionSection heading="If you're in immediate danger">
          <p>
            Call{" "}
            <a href="tel:911" className="text-accent font-bold hover:underline">
              911
            </a>{" "}
            or go to your nearest emergency room now. Do not wait.
          </p>
        </DecisionSection>

        <DecisionSection heading="If you're in crisis but not in immediate danger">
          <p>
            Call or text{" "}
            <a href="tel:988" className="text-accent font-bold hover:underline">
              988
            </a>{" "}
            — the Suicide and Crisis Lifeline. It's available 24 hours a day, 7
            days a week, and it's free. You don't have to be suicidal to call.
            If you're struggling, that's reason enough.
          </p>
          <p>
            You can also text 988. Texting is available and may feel easier if
            you can't speak right now.
          </p>
        </DecisionSection>

        <DecisionSection heading="Other resources">
          <DecisionList
            items={[
              "Crisis Text Line: Text HOME to 741741",
              "Veterans Crisis Line: Dial 988, then press 1, or text 838255",
              "National Domestic Violence Hotline: 1-800-799-7233",
              "Florida DCF Abuse Hotline: 1-800-962-2873",
            ]}
          />
        </DecisionSection>

        <DecisionSection heading="If you're having thoughts of suicide">
          <p>
            Suicidal thoughts are a symptom of depression, not a permanent part
            of who you are. They can be treated. If you're having these
            thoughts, please reach out to 988 right now. You don't have to be
            alone with this.
          </p>
          <p>
            Neuro Wellness TMS Centers of America offers SPRAVATO (esketamine),
            which is FDA-approved for adults with major depressive disorder who
            are experiencing suicidal thoughts or actions.{" "}
            <Link
              to="/services/spravato"
              className="text-accent hover:underline"
            >
              Learn about SPRAVATO
            </Link>
            .
          </p>
          <p>
            But treatment can wait if you need help right now. Call 988 first.
          </p>
        </DecisionSection>

        <DecisionSection heading="For family and friends">
          <p>
            If someone you know is in crisis, stay with them. Call 988 together.
            Remove access to means if you can do so safely. Don't leave them
            alone. And don't promise to keep their thoughts a secret — getting
            help matters more than keeping a confidence.
          </p>
        </DecisionSection>
      </div>

      <InlineRelatedLinks heading="Related Pages" links={relatedLinks} />
    </DecisionPageLayout>
  );
}
